import {
  CategoryType,
  Mission,
  useMissionDetail,
  useTradeMission,
} from '@/app/hooks/trade/tradeMission';
import { ThemeContext } from '@/theme/ThemeProvider';
import BigNumber from 'bignumber.js';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToken } from '@/app/hooks/auth/token';
import { useMyProfile } from '@/app/hooks/user/myprofile';
import { DisabledRewardButton } from '../../components/DisabledRewardButton';
import {
  canClaimable,
  getClaimStatus,
  getMissionTemplateCode,
  MissionCode,
} from '../../utils';
import * as S from './styles';
import useNavigationHandler from '../../useNavigationHandler';
import RewardLoading from '../RewardLoading';

// Local assets
import rocketEmojiImg from '@/assets/images/reward-page/rocket-emoji.svg';
import amountBadgeImg from '@/assets/images/reward-page/amount-badge.svg';
import amountBadge2Img from '@/assets/images/reward-page/amount-badge-2.svg';
import giftIconImg from '@/assets/images/reward-page/gift-icon.svg';
import diamondIconImg from '@/assets/images/reward-page/diamond-icon.svg';
import progressBarBgImg from '@/assets/images/reward-page/progress-bar-bg.svg';
import progressBarFillImg from '@/assets/images/reward-page/progress-bar-fill.svg';

interface DetailMissions {
  register: Mission[];
  firstDeposit: Mission[];
}

const NewUser = () => {
  const [detailMissions, setDetailMissions] = useState<DetailMissions>({
    register: [],
    firstDeposit: [],
  });

  const { data, getMissionReward, isLoading } = useTradeMission(
    CategoryType.ONBOARDING
  );
  const { isDarkMode } = useContext(ThemeContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useToken();
  const { data: myProfile } = useMyProfile();
  const onboardingMissions = data?.missions ?? [];
  const handleNavigation = useNavigationHandler();

  // 첫 입금 미션 상세 조회
  const firstDepositMissionId = detailMissions.firstDeposit[0]?.id;
  const { data: detailFirstDepositMission } = useMissionDetail(
    firstDepositMissionId
  );

  // 신규 사용자를 위한 미션 중, 가입 보상과 첫 입금 미션을 분리합니다.
  useEffect(() => {
    if (!onboardingMissions || !onboardingMissions.length) return;

    setDetailMissions({
      register: onboardingMissions.filter(
        (mission) =>
          getMissionTemplateCode(mission.code) === MissionCode.SIGNUP_BONUS ||
          getMissionTemplateCode(mission.code) === MissionCode.KYC_COMPLETE
      ),
      firstDeposit: onboardingMissions.filter(
        (mission) =>
          getMissionTemplateCode(mission.code) ===
          MissionCode.FIRST_DEPOSIT_PROMO
      ),
    });
  }, [onboardingMissions]);

  if (isLoading || !detailMissions.register.length) {
    return <RewardLoading messageKey="loadingNewUserMission" />;
  }

  const registerMission = detailMissions.register[0];
  const firstDepositMission = detailMissions.firstDeposit[0];
  const firstTradeMission = onboardingMissions.find(
    (mission) =>
      getMissionTemplateCode(mission.code) === MissionCode.FIRST_TRADE
  );
  const galaxyTradeMission = onboardingMissions.find(
    (mission) =>
      getMissionTemplateCode(mission.code) === MissionCode.GALAXY_TRADE
  );

  return (
    <S.WelcomeRewardsSection>
      <S.SectionContainer>
        {/* Tab Menu */}
        <S.TabMenu>
          <S.TabItem $active={true}>New User Rewards</S.TabItem>
          <S.TabItem $active={false}>Advanced Challenges</S.TabItem>
          <S.TabLine />
        </S.TabMenu>

        {/* Title */}
        <S.TitleSection>
          <S.RocketIcon>
            <img src={rocketEmojiImg} alt="rocket" />
          </S.RocketIcon>
          <S.TitleText>
            Earn up to <S.HighlightText>120 USDT</S.HighlightText> in Welcome Rewards
          </S.TitleText>
        </S.TitleSection>

        {/* Reward Cards Grid */}
        <S.CardsGrid>
          {/* Card 1: Welcome Aboard Bonus */}
          {registerMission && (
            <S.RewardCard>
              <S.RewardAmountBox>
                <S.AmountImage src={amountBadgeImg} alt="amount" />
                <S.AmountContent>
                  <S.AmountValue>{new BigNumber(registerMission.rewardAmount).toFormat()}</S.AmountValue>
                  <S.AmountUnit>USDT</S.AmountUnit>
                </S.AmountContent>
                <S.AmountLabel>Launch Bonus</S.AmountLabel>
              </S.RewardAmountBox>
              <S.CardContent>
                <S.MissionInfo>
                  <S.MissionType>Registration Mission</S.MissionType>
                  <S.MissionTitle>Welcome Aboard Bonus</S.MissionTitle>
                </S.MissionInfo>
                <S.MissionDescription>
                  Create your Piecrip account and activate your profile to receive your first reward.
                </S.MissionDescription>
                {getClaimStatus(registerMission, 0) === 'claimed' ? (
                  <S.ClaimedButton disabled>
                    {t('Rewarded')}
                  </S.ClaimedButton>
                ) : canClaimable(registerMission, 0) ? (
                  <S.ClaimButton onClick={() =>
                    getMissionReward(
                      registerMission.id,
                      undefined,
                      registerMission.rewardAmount
                    )
                  }>
                    <S.GiftIcon>
                      <img src={giftIconImg} alt="gift" />
                    </S.GiftIcon>
                    Claim
                  </S.ClaimButton>
                ) : (
                  <DisabledRewardButton
                    variant="orange"
                    onClick={() => handleNavigation('/settings?tab=2')}
                    style={{ height: '42px', borderRadius: '30px' }}
                  >
                    {t('verifyNow')}
                  </DisabledRewardButton>
                )}
              </S.CardContent>
            </S.RewardCard>
          )}

          {/* Card 2: Deposit to Unlock Rewards */}
          {firstDepositMission && detailFirstDepositMission?.conditions?.tiers?.[0] && (
            <S.RewardCard>
              <S.RewardAmountBox>
                <S.AmountImage src={amountBadge2Img} alt="amount" />
                <S.AmountContent>
                  <S.AmountValue>
                    {new BigNumber(detailFirstDepositMission.conditions.tiers[0].rewardAmount).toFormat()}
                  </S.AmountValue>
                  <S.AmountUnit>USDT</S.AmountUnit>
                </S.AmountContent>
                <S.AmountLabel>Deposit Bonus</S.AmountLabel>
              </S.RewardAmountBox>
              <S.CardContent>
                <S.MissionInfo>
                  <S.MissionType>First Deposit Mission</S.MissionType>
                  <S.MissionTitle>Deposit to Unlock Rewards</S.MissionTitle>
                </S.MissionInfo>
                <S.MissionDescription>
                  Make your first deposit within the mission period and receive a bonus to kickstart your voyage.
                </S.MissionDescription>
                {getClaimStatus(firstDepositMission, 0) === 'claimed' ? (
                  <S.ClaimedButton disabled>
                    {t('Rewarded')}
                  </S.ClaimedButton>
                ) : canClaimable(firstDepositMission, 0) ? (
                  <S.ClaimButton onClick={() =>
                    getMissionReward(
                      firstDepositMission.id,
                      0,
                      detailFirstDepositMission?.conditions?.tiers?.[0]?.rewardAmount
                    )
                  }>
                    <S.DiamondIcon>
                      <img src={diamondIconImg} alt="diamond" />
                    </S.DiamondIcon>
                    Deposit
                  </S.ClaimButton>
                ) : (
                  <DisabledRewardButton
                    variant="orange"
                    onClick={() => handleNavigation('/buysell-crypto')}
                    style={{ height: '42px', borderRadius: '30px' }}
                  >
                    Deposit
                  </DisabledRewardButton>
                )}
              </S.CardContent>
            </S.RewardCard>
          )}

          {/* Card 3: Make Your First Orbit */}
          {firstTradeMission && (
            <S.RewardCard $hasBadge>
              <S.LimitedBadge>Limited-time Task 08:22:20:02</S.LimitedBadge>
              <S.RewardAmountBox>
                <S.AmountImage src={amountBadge2Img} alt="amount" />
                <S.AmountContent $isCredit>
                  <S.AmountValue $isCredit>Bonus</S.AmountValue>
                  <S.AmountUnit $isCredit>Credit</S.AmountUnit>
                </S.AmountContent>
                <S.AmountLabel>for Futures/Spot</S.AmountLabel>
              </S.RewardAmountBox>
              <S.CardContent>
                <S.MissionInfo>
                  <S.MissionType>First Trade Mission</S.MissionType>
                  <S.MissionTitle>Make Your First Orbit</S.MissionTitle>
                </S.MissionInfo>
                <S.ProgressSection>
                  <S.ProgressText>
                    Traded <S.ProgressValue>0</S.ProgressValue> / 500 USDT
                  </S.ProgressText>
                  <S.ProgressBar>
                    <S.ProgressBarBg src={progressBarBgImg} />
                  </S.ProgressBar>
                </S.ProgressSection>
                {getClaimStatus(firstTradeMission, 0) === 'claimed' ? (
                  <S.ClaimedButton disabled>
                    {t('Rewarded')}
                  </S.ClaimedButton>
                ) : canClaimable(firstTradeMission, 0) ? (
                  <S.TradeButton onClick={() =>
                    getMissionReward(
                      firstTradeMission.id,
                      undefined,
                      firstTradeMission.rewardAmount
                    )
                  }>
                    Trade
                  </S.TradeButton>
                ) : (
                  <DisabledRewardButton
                    variant="orange"
                    onClick={() => handleNavigation('/trade')}
                    style={{ height: '42px', borderRadius: '30px' }}
                  >
                    Trade
                  </DisabledRewardButton>
                )}
              </S.CardContent>
            </S.RewardCard>
          )}

          {/* Card 4: Galaxy Trading Boost */}
          {galaxyTradeMission && (
            <S.RewardCard $hasBadge>
              <S.LimitedBadge>Limited-time Task 08:22:20:02</S.LimitedBadge>
              <S.RewardAmountBox>
                <S.AmountImage src={amountBadge2Img} alt="amount" />
                <S.AmountContent $isGalaxy>
                  <S.AmountValue $isGalaxy>20</S.AmountValue>
                  <S.AmountUnit $isGalaxy>USDT</S.AmountUnit>
                </S.AmountContent>
                <S.AmountLabel>Galaxy Bonus</S.AmountLabel>
              </S.RewardAmountBox>
              <S.CardContent>
                <S.MissionInfo>
                  <S.MissionType>First Trade Mission</S.MissionType>
                  <S.MissionTitle>Galaxy Trading Boost</S.MissionTitle>
                </S.MissionInfo>
                <S.ProgressSection>
                  <S.ProgressText>
                    Traded <S.ProgressValue>6</S.ProgressValue> / 500 USDT
                  </S.ProgressText>
                  <S.ProgressBar>
                    <S.ProgressBarBg src={progressBarBgImg} />
                    <S.ProgressBarFill src={progressBarFillImg} $progress={6 / 500} />
                  </S.ProgressBar>
                </S.ProgressSection>
                {getClaimStatus(galaxyTradeMission, 0) === 'claimed' ? (
                  <S.ClaimedButton disabled>
                    {t('Rewarded')}
                  </S.ClaimedButton>
                ) : canClaimable(galaxyTradeMission, 0) ? (
                  <S.TradeButton onClick={() =>
                    getMissionReward(
                      galaxyTradeMission.id,
                      undefined,
                      galaxyTradeMission.rewardAmount
                    )
                  }>
                    Trade
                  </S.TradeButton>
                ) : (
                  <DisabledRewardButton
                    variant="orange"
                    onClick={() => handleNavigation('/trade')}
                    style={{ height: '42px', borderRadius: '30px' }}
                  >
                    Trade
                  </DisabledRewardButton>
                )}
              </S.CardContent>
            </S.RewardCard>
          )}
        </S.CardsGrid>
      </S.SectionContainer>
    </S.WelcomeRewardsSection>
  );
};

export default NewUser;
